import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { useNotifications } from "../../context/NotificationContext";
import { getNotificationIcon, formatRelativeTime, resolveNotificationRoute } from "../../utils/notificationHelpers";
import EmptyState from "../common/EmptyState";
import { color, radius, shadow } from "../../styles/theme";

const DROPDOWN_LIMIT = 8;

const NotificationBell = () => {
  const { t } = useTranslation("notifications");
  const navigate = useNavigate();
  const notif = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Lazy-load the list only when the dropdown is actually opened — the
  // list isn't fetched eagerly in the background (see NotificationContext),
  // since fetching it is what marks items "seen" server-side and would
  // clear the badge before the user ever looked at it.
  useEffect(() => {
    if (open) notif?.load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!notif) return null;
  const { notifications, unreadCount, markRead, markAllRead } = notif;
  const recent = notifications.slice(0, DROPDOWN_LIMIT);

  const handleItemClick = (item) => {
    if (!item.IsRead) markRead(item.Id);
    setOpen(false);
    const route = resolveNotificationRoute(item);
    if (route) navigate(route);
    else navigate("/notifications");
  };

  return (
    <div ref={ref} style={s.wrap}>
      <button type="button" style={s.trigger} onClick={() => setOpen((v) => !v)} aria-label={t("bell.label")}>
        <Bell size={16} strokeWidth={2} />
        {unreadCount > 0 && <span style={s.dot} />}
      </button>

      {open && (
        <div className="modal-pop" style={s.dropdown}>
          <div style={s.header}>
            <span style={s.headerTitle}>{t("bell.title")}</span>
            {unreadCount > 0 && (
              <button type="button" style={s.markAllBtn} onClick={markAllRead}>{t("bell.markAllRead")}</button>
            )}
          </div>

          <div style={s.list}>
            {recent.length === 0 ? (
              <div style={{ padding: "18px 16px" }}>
                <EmptyState icon={Bell} text={t("bell.empty")} />
              </div>
            ) : (
              recent.map((item) => {
                const Icon = getNotificationIcon(item.Notification?.CategoryName);
                return (
                  <button
                    key={item.Id}
                    type="button"
                    className="row-link"
                    style={{ ...s.item, ...(item.IsRead ? {} : s.itemUnread) }}
                    onClick={() => handleItemClick(item)}
                  >
                    <span style={s.itemIcon}><Icon size={15} strokeWidth={2} /></span>
                    <span style={s.itemBody}>
                      <span style={{ ...s.itemTitle, ...(item.IsRead ? {} : s.itemTitleUnread) }}>
                        {item.Notification?.Title}
                      </span>
                      <span style={s.itemDesc}>{item.Notification?.Description}</span>
                      <span style={s.itemTime}>{formatRelativeTime(item.Notification?.CreationDate, t)}</span>
                    </span>
                    {!item.IsRead && <span style={s.itemDot} />}
                  </button>
                );
              })
            )}
          </div>

          <button type="button" style={s.viewAll} onClick={() => { setOpen(false); navigate("/notifications"); }}>
            {t("bell.viewAll")}
          </button>
        </div>
      )}
    </div>
  );
};

const s = {
  wrap: { position: "relative" },
  trigger: {
    position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
    width: 32, height: 32, background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.09)", borderRadius: radius.sm,
    cursor: "pointer", color: "#d1d5db", transition: "background 150ms",
  },
  dot: {
    position: "absolute", top: 6, right: 6, width: 7, height: 7, borderRadius: "50%",
    background: color.danger500, border: "1.5px solid " + color.gray900,
  },
  dropdown: {
    position: "absolute", top: "calc(100% + 8px)", right: 0, width: 340,
    background: color.surface, borderRadius: radius.md, boxShadow: shadow.lg,
    border: `1px solid ${color.border}`, overflow: "hidden", zIndex: 200,
    display: "flex", flexDirection: "column", maxHeight: 440,
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "13px 16px", borderBottom: `1px solid ${color.border}`, flexShrink: 0,
  },
  headerTitle: { fontWeight: 700, fontSize: 14, color: color.textPrimary },
  markAllBtn: {
    background: "none", border: "none", color: color.primary600, fontSize: 12,
    fontWeight: 600, cursor: "pointer", padding: 0,
  },
  list: { overflowY: "auto", flex: 1 },
  item: {
    width: "100%", display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px",
    background: "transparent", border: "none", borderBottom: `1px solid ${color.gray100}`,
    cursor: "pointer", textAlign: "left", position: "relative",
  },
  itemUnread: { background: color.primary50 },
  itemIcon: {
    width: 28, height: 28, borderRadius: radius.sm, background: color.gray100, color: color.gray700,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1,
  },
  itemBody: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 },
  itemTitle: { fontSize: 12.5, fontWeight: 500, color: color.gray700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  itemTitleUnread: { fontWeight: 700, color: color.textPrimary },
  itemDesc: {
    fontSize: 11.5, color: color.textMuted, overflow: "hidden", textOverflow: "ellipsis",
    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
  },
  itemTime: { fontSize: 10.5, color: color.textMuted, marginTop: 1 },
  itemDot: { width: 7, height: 7, borderRadius: "50%", background: color.primary500, flexShrink: 0, marginTop: 6 },
  viewAll: {
    width: "100%", padding: "11px 16px", background: color.gray50, border: "none",
    borderTop: `1px solid ${color.border}`, color: color.primary600, fontSize: 12.5,
    fontWeight: 700, cursor: "pointer", flexShrink: 0,
  },
};

export default NotificationBell;
